// ABOUTME: Seasonal color palette definitions for personal styling
// ABOUTME: Maps skin tones to seasonal palettes with hex colors and recommendations

export type SkinTone = 'light' | 'medium' | 'dark'
export type Undertone = 'warm' | 'cool' | 'neutral'
export type Season = 'spring' | 'summer' | 'autumn' | 'winter'

export interface ColorInfo {
  name: string
  hex: string
  category: 'primary' | 'secondary' | 'neutral' | 'accent'
}

export interface SeasonalPalette {
  season: Season
  title: string
  description: string
  characteristics: string[]
  colors: ColorInfo[]
  bestFor: string[]
  avoid: string[]
}

/**
 * Determine season based on skin tone and undertone
 */
export function determineSeasonFromProfile(skinTone: SkinTone, undertone: Undertone): Season {
  // Spring: Light + Warm
  if (skinTone === 'light' && undertone === 'warm') return 'spring'

  // Summer: Light + Cool
  if (skinTone === 'light' && undertone === 'cool') return 'summer'

  // Autumn: Medium/Dark + Warm
  if ((skinTone === 'medium' || skinTone === 'dark') && undertone === 'warm') return 'autumn'

  // Winter: Medium/Dark + Cool
  if ((skinTone === 'medium' || skinTone === 'dark') && undertone === 'cool') return 'winter'

  // Neutral undertone - default based on skin tone
  if (undertone === 'neutral') {
    if (skinTone === 'light') return 'summer'
    if (skinTone === 'medium') return 'autumn'
    return 'winter'
  }

  return 'autumn' // Default fallback
}

/**
 * Seasonal color palettes
 */
export const SEASONAL_PALETTES: Record<Season, SeasonalPalette> = {
  spring: {
    season: 'spring',
    title: 'Spring',
    description: 'Warm, clear, and bright colors that reflect the fresh energy of spring. Best for light skin with warm undertones.',
    characteristics: [
      'Warm and bright tones',
      'Clear and vibrant colors',
      'Light to medium saturation',
      'Golden and peachy undertones',
    ],
    colors: [
      // Primary colors
      { name: 'Warm Beige', hex: '#E8D5C4', category: 'primary' },
      { name: 'Camel', hex: '#C19A6B', category: 'primary' },
      { name: 'Warm Taupe', hex: '#B38B6D', category: 'primary' },

      // Secondary colors
      { name: 'Coral Pink', hex: '#F88379', category: 'secondary' },
      { name: 'Peach', hex: '#FFDAB9', category: 'secondary' },
      { name: 'Salmon', hex: '#FA8072', category: 'secondary' },
      { name: 'Warm Red', hex: '#E74C3C', category: 'secondary' },

      { name: 'Golden Yellow', hex: '#FFD700', category: 'secondary' },
      { name: 'Warm Orange', hex: '#FF8C42', category: 'secondary' },

      { name: 'Warm Green', hex: '#8FBC8F', category: 'secondary' },
      { name: 'Kelly Green', hex: '#4CBB17', category: 'secondary' },
      { name: 'Turquoise', hex: '#40E0D0', category: 'secondary' },

      { name: 'Warm Blue', hex: '#5DADE2', category: 'secondary' },
      { name: 'Aqua', hex: '#00CED1', category: 'secondary' },

      // Neutrals
      { name: 'Ivory', hex: '#FFFFF0', category: 'neutral' },
      { name: 'Cream', hex: '#FFFDD0', category: 'neutral' },
      { name: 'Light Gray', hex: '#D3D3D3', category: 'neutral' },
      { name: 'Warm Brown', hex: '#8B4513', category: 'neutral' },

      // Accents
      { name: 'Clear Navy', hex: '#1E3A8A', category: 'accent' },
      { name: 'Bronze', hex: '#CD7F32', category: 'accent' },
      { name: 'Gold', hex: '#FFD700', category: 'accent' },
    ],
    bestFor: [
      'Light skin with golden/peachy undertones',
      'Hair: Blonde, light brown, red with golden tones',
      'Eyes: Blue, green, hazel, light brown',
    ],
    avoid: [
      'Black (too harsh)',
      'Pure white',
      'Cool jewel tones',
      'Heavy, dark colors',
    ],
  },

  summer: {
    season: 'summer',
    title: 'Summer',
    description: 'Cool, soft, and muted colors like a hazy summer day. Best for light to medium skin with cool undertones.',
    characteristics: [
      'Cool and soft tones',
      'Muted and gentle colors',
      'Blue or pink undertones',
      'Dusty and powdery shades',
    ],
    colors: [
      // Primary colors
      { name: 'Soft White', hex: '#F8F8FF', category: 'primary' },
      { name: 'Pearl Gray', hex: '#E6E6FA', category: 'primary' },
      { name: 'Cool Taupe', hex: '#B38E8E', category: 'primary' },

      // Secondary colors
      { name: 'Rose Pink', hex: '#FFB6C1', category: 'secondary' },
      { name: 'Dusty Rose', hex: '#DCAE96', category: 'secondary' },
      { name: 'Raspberry', hex: '#E30B5C', category: 'secondary' },
      { name: 'Cool Red', hex: '#DC143C', category: 'secondary' },

      { name: 'Lemon Yellow', hex: '#FFFACD', category: 'secondary' },
      { name: 'Soft Pink', hex: '#FFB6D9', category: 'secondary' },

      { name: 'Sage Green', hex: '#9CAF88', category: 'secondary' },
      { name: 'Mint', hex: '#98FF98', category: 'secondary' },
      { name: 'Seafoam', hex: '#93E9BE', category: 'secondary' },

      { name: 'Powder Blue', hex: '#B0E0E6', category: 'secondary' },
      { name: 'Periwinkle', hex: '#CCCCFF', category: 'secondary' },
      { name: 'Sky Blue', hex: '#87CEEB', category: 'secondary' },

      { name: 'Lavender', hex: '#E6E6FA', category: 'secondary' },
      { name: 'Mauve', hex: '#E0B0FF', category: 'secondary' },

      // Neutrals
      { name: 'Soft White', hex: '#FAF9F6', category: 'neutral' },
      { name: 'Cool Gray', hex: '#AEB6BF', category: 'neutral' },
      { name: 'Charcoal', hex: '#36454F', category: 'neutral' },
      { name: 'Navy', hex: '#000080', category: 'neutral' },

      // Accents
      { name: 'Burgundy', hex: '#800020', category: 'accent' },
      { name: 'Plum', hex: '#8E4585', category: 'accent' },
      { name: 'Silver', hex: '#C0C0C0', category: 'accent' },
    ],
    bestFor: [
      'Light to medium skin with pink/blue undertones',
      'Hair: Ash blonde, brown, gray',
      'Eyes: Blue, gray, hazel, soft brown',
    ],
    avoid: [
      'Orange and rust',
      'Warm yellows',
      'Golden tones',
      'Bright, clear colors',
    ],
  },

  autumn: {
    season: 'autumn',
    title: 'Autumn',
    description: 'Rich, warm, and earthy colors like autumn leaves. Best for medium to dark skin with warm undertones.',
    characteristics: [
      'Warm and rich tones',
      'Earthy and muted colors',
      'Golden and rust undertones',
      'Deep and saturated shades',
    ],
    colors: [
      // Primary colors
      { name: 'Cream', hex: '#FFFDD0', category: 'primary' },
      { name: 'Camel', hex: '#C19A6B', category: 'primary' },
      { name: 'Chocolate Brown', hex: '#7B3F00', category: 'primary' },

      // Secondary colors
      { name: 'Rust', hex: '#B7410E', category: 'secondary' },
      { name: 'Terracotta', hex: '#E2725B', category: 'secondary' },
      { name: 'Burnt Orange', hex: '#CC5500', category: 'secondary' },
      { name: 'Brick Red', hex: '#CB4154', category: 'secondary' },

      { name: 'Mustard', hex: '#FFDB58', category: 'secondary' },
      { name: 'Gold', hex: '#FFD700', category: 'secondary' },
      { name: 'Amber', hex: '#FFBF00', category: 'secondary' },

      { name: 'Olive', hex: '#808000', category: 'secondary' },
      { name: 'Forest Green', hex: '#228B22', category: 'secondary' },
      { name: 'Moss', hex: '#8A9A5B', category: 'secondary' },
      { name: 'Teal', hex: '#008080', category: 'secondary' },

      { name: 'Warm Brown', hex: '#8B4513', category: 'secondary' },
      { name: 'Cognac', hex: '#9A463D', category: 'secondary' },

      // Neutrals
      { name: 'Warm Beige', hex: '#F5DEB3', category: 'neutral' },
      { name: 'Khaki', hex: '#C3B091', category: 'neutral' },
      { name: 'Warm Gray', hex: '#8C8C8C', category: 'neutral' },
      { name: 'Deep Brown', hex: '#654321', category: 'neutral' },

      // Accents
      { name: 'Burgundy', hex: '#800020', category: 'accent' },
      { name: 'Copper', hex: '#B87333', category: 'accent' },
      { name: 'Bronze', hex: '#CD7F32', category: 'accent' },
    ],
    bestFor: [
      'Medium to dark skin with golden/warm undertones',
      'Hair: Red, auburn, warm brown, golden blonde',
      'Eyes: Brown, green, hazel, warm amber',
    ],
    avoid: [
      'Black (too harsh)',
      'Icy pastels',
      'Bright pinks',
      'Cool blues and purples',
    ],
  },

  winter: {
    season: 'winter',
    title: 'Winter',
    description: 'Cool, clear, and dramatic colors with high contrast. Best for medium to dark skin with cool undertones.',
    characteristics: [
      'Cool and clear tones',
      'High contrast colors',
      'Blue or pink undertones',
      'Bold and vivid shades',
    ],
    colors: [
      // Primary colors
      { name: 'Pure White', hex: '#FFFFFF', category: 'primary' },
      { name: 'Black', hex: '#000000', category: 'primary' },
      { name: 'Charcoal', hex: '#36454F', category: 'primary' },

      // Secondary colors
      { name: 'True Red', hex: '#FF0000', category: 'secondary' },
      { name: 'Magenta', hex: '#FF00FF', category: 'secondary' },
      { name: 'Hot Pink', hex: '#FF69B4', category: 'secondary' },
      { name: 'Fuchsia', hex: '#FF00FF', category: 'secondary' },

      { name: 'Icy Yellow', hex: '#FFFFE0', category: 'secondary' },
      { name: 'Lemon', hex: '#FFF44F', category: 'secondary' },

      { name: 'Emerald', hex: '#50C878', category: 'secondary' },
      { name: 'Pine Green', hex: '#01796F', category: 'secondary' },
      { name: 'Icy Blue', hex: '#99FFFF', category: 'secondary' },

      { name: 'Royal Blue', hex: '#4169E1', category: 'secondary' },
      { name: 'Navy', hex: '#000080', category: 'secondary' },
      { name: 'Electric Blue', hex: '#7DF9FF', category: 'secondary' },

      { name: 'Purple', hex: '#800080', category: 'secondary' },
      { name: 'Violet', hex: '#8F00FF', category: 'secondary' },

      // Neutrals
      { name: 'Pure White', hex: '#FFFFFF', category: 'neutral' },
      { name: 'Cool Gray', hex: '#8B8B8B', category: 'neutral' },
      { name: 'Taupe', hex: '#483C32', category: 'neutral' },
      { name: 'Black', hex: '#000000', category: 'neutral' },

      // Accents
      { name: 'Silver', hex: '#C0C0C0', category: 'accent' },
      { name: 'Platinum', hex: '#E5E4E2', category: 'accent' },
      { name: 'Icy Pink', hex: '#FFB6D9', category: 'accent' },
    ],
    bestFor: [
      'Medium to dark skin with blue/pink undertones',
      'Hair: Dark brown, black, gray, platinum blonde',
      'Eyes: Dark brown, black, gray-blue, ice blue',
    ],
    avoid: [
      'Warm browns',
      'Orange and rust',
      'Golden yellows',
      'Muted, dusty colors',
    ],
  },
}

/**
 * Get palette by season
 */
export function getPaletteForSeason(season: Season): SeasonalPalette {
  return SEASONAL_PALETTES[season]
}

/**
 * Get color recommendations for a season
 */
export function getColorRecommendations(season: Season, category?: ColorInfo['category']): ColorInfo[] {
  const palette = SEASONAL_PALETTES[season]

  if (category) {
    return palette.colors.filter(c => c.category === category)
  }

  return palette.colors
}

/**
 * Check if color matches user's season
 */
export function isColorInSeason(colorName: string, season: Season): boolean {
  const palette = SEASONAL_PALETTES[season]
  const lowerColorName = colorName.toLowerCase()

  return palette.colors.some(c =>
    c.name.toLowerCase().includes(lowerColorName) ||
    lowerColorName.includes(c.name.toLowerCase())
  )
}

/**
 * Get complementary colors for a given season
 */
export function getComplementaryColors(season: Season, baseColor: string): ColorInfo[] {
  const palette = SEASONAL_PALETTES[season]
  const lowerBase = baseColor.toLowerCase()

  // Find the base color
  const base = palette.colors.find(c =>
    c.name.toLowerCase().includes(lowerBase) ||
    lowerBase.includes(c.name.toLowerCase())
  )

  if (!base) {
    // Return neutral colors if base not found
    return palette.colors.filter(c => c.category === 'neutral')
  }

  // Return colors that complement the base
  // Neutrals go with everything
  const neutrals = palette.colors.filter(c => c.category === 'neutral')

  // Add some accent colors
  const accents = palette.colors.filter(c => c.category === 'accent').slice(0, 2)

  return [...neutrals, ...accents]
}
